import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import GroupChip from '../components/GroupChip';
import PersonFilterBar from '../components/PersonFilterBar';
import FeedWishCard, { giftStatus } from '../components/FeedWishCard';
import SuggestIdeaModal from '../components/SuggestIdeaModal';
import { revealAfterBirthday } from '../utils/dates';
import { wishOwner, ownerKey } from '../utils/wishOwner';

// The home page: a "shop for others" feed of giftable wishes across ALL the
// user's groups (GET /wishes/feed). Each card shows the owner, the wish, the
// group(s) it's in (icon + color chips), any tied event, gift status, and
// claim/purchase actions. Mirrors the iOS Shop tab.

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

export default function Feed() {
    const { user } = useAuth();

    const [wishes, setWishes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState(null); // { type, text }
    const [filterGroupId, setFilterGroupId] = useState(null);
    const [filterOwnerKey, setFilterOwnerKey] = useState(null);
    const [busyId, setBusyId] = useState(null);

    // Purchase modal state.
    const [purchaseTarget, setPurchaseTarget] = useState(null);
    const [purchaseNote, setPurchaseNote] = useState('');
    const [revealDate, setRevealDate] = useState('');

    // "Suggest to…" compose modal, prefilled from a browsed item.
    const [suggestFrom, setSuggestFrom] = useState(null); // the wish being suggested

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/wishes/feed');
            setWishes(res.data);
            setError('');
        } catch (err) {
            setError(errText(err, 'Failed to load the feed.'));
        } finally {
            setLoading(false);
        }
    };

    const notify = (type, text) => {
        setNotice({ type, text });
        setTimeout(() => setNotice(null), 3000);
    };

    // Distinct groups present in the feed (for the filter row).
    const filterGroups = (() => {
        const seen = new Map();
        for (const w of wishes) {
            for (const g of w.groups || []) {
                if (!seen.has(g.id)) seen.set(g.id, g);
            }
        }
        return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    })();

    // The two filters compose: group AND recipient. The person chips are built
    // from the group-filtered set, so they only ever offer recipients present in
    // the current group.
    const groupFiltered = filterGroupId
        ? wishes.filter((w) => (w.groups || []).some((g) => g.id === filterGroupId))
        : wishes;

    // Changing the group can strand the person filter on someone who isn't in the
    // new group: the chip stops rendering, but the filter would still apply, and
    // the user would be staring at an empty list with no visible control to clear
    // it. Treat a stranded selection as "everyone".
    const ownerStillPresent = groupFiltered.some((w) => ownerKey(w) === filterOwnerKey);
    const activeOwnerKey = ownerStillPresent ? filterOwnerKey : null;

    const visible = activeOwnerKey
        ? groupFiltered.filter((w) => ownerKey(w) === activeOwnerKey)
        : groupFiltered;

    const selectGroup = (groupId) => {
        setFilterGroupId(groupId);
        setFilterOwnerKey(null);
    };

    // Which group to claim/purchase within (the first of the viewer's groups the
    // wish is shared into; the backend resolves membership from it).
    const groupIdFor = (wish) => wish.groups?.[0]?.id;

    const claim = async (wish) => {
        setBusyId(wish.id);
        try {
            await api.post(`/wishes/${wish.id}/claim`, { group_id: groupIdFor(wish) });
            notify('success', 'Claimed — others now see it as reserved.');
            await load();
        } catch (err) {
            notify('error', errText(err, 'Could not claim the wish.'));
        } finally {
            setBusyId(null);
        }
    };

    const unclaim = async (wish) => {
        setBusyId(wish.id);
        try {
            await api.delete(`/wishes/${wish.id}/claim`);
            notify('success', 'Claim released.');
            await load();
        } catch (err) {
            notify('error', errText(err, 'Could not release the claim.'));
        } finally {
            setBusyId(null);
        }
    };

    const openPurchase = (wish) => {
        setPurchaseTarget(wish);
        setPurchaseNote('');
        // For a pet wish, the "recipient" is the pet — use its birthday.
        const reveal = revealAfterBirthday(wishOwner(wish).birthday);
        setRevealDate(reveal ? reveal.iso : '');
    };

    const confirmPurchase = async () => {
        const wish = purchaseTarget;
        setBusyId(wish.id);
        try {
            await api.post(`/wishes/${wish.id}/purchase`, {
                group_id: groupIdFor(wish),
                private_note: purchaseNote || null,
                reveal_date: revealDate || null,
            });
            notify('success', 'Marked as purchased. The owner will not see this.');
            setPurchaseTarget(null);
            await load();
        } catch (err) {
            notify('error', errText(err, 'Could not record the purchase.'));
        } finally {
            setBusyId(null);
        }
    };

    const undoPurchase = async (wish) => {
        setBusyId(wish.id);
        try {
            await api.delete(`/wishes/${wish.id}/purchase`);
            notify('success', 'Purchase undone.');
            await load();
        } catch (err) {
            notify('error', errText(err, 'Could not undo the purchase.'));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <AppLayout>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Shop for others</h2>

                {notice && (
                    <div className={`mb-4 p-3 rounded-md text-sm border ${notice.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                        {notice.text}
                    </div>
                )}
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>
                )}

                {/* Group filter */}
                {filterGroups.length >= 2 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                        <FilterButton label="All" active={filterGroupId === null} onClick={() => selectGroup(null)} />
                        {filterGroups.map((g) => (
                            <button key={g.id} onClick={() => selectGroup(g.id)} className="focus:outline-none">
                                <span className={filterGroupId === g.id ? 'ring-2 ring-offset-1 ring-gray-300 rounded-full inline-block' : 'inline-block'}>
                                    <GroupChip name={g.name} icon={g.icon} color={g.color} seed={g.id} />
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Recipient filter — "who am I shopping for?" */}
                <PersonFilterBar
                    wishes={groupFiltered}
                    value={activeOwnerKey}
                    onChange={setFilterOwnerKey}
                    currentUserId={user?.id}
                />

                {loading ? (
                    <div className="text-center py-12 text-gray-500">Loading…</div>
                ) : visible.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                        Nothing to shop for yet — when people in your groups add wishes, they'll show up here.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {visible.map((wish) => (
                            <FeedWishCard
                                key={wish.id}
                                wish={wish}
                                user={user}
                                status={giftStatus(wish, user?.id)}
                                busy={busyId === wish.id}
                                onClaim={() => claim(wish)}
                                onUnclaim={() => unclaim(wish)}
                                onPurchase={() => openPurchase(wish)}
                                onUndo={() => undoPurchase(wish)}
                                onSuggest={() => setSuggestFrom(wish)}
                            />
                        ))}
                    </div>
                )}

            {/* Purchase modal */}
            {purchaseTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-1">Mark as purchased</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            “{purchaseTarget.title}”. The owner will never see this.
                        </p>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Private note (optional)</label>
                        <input
                            type="text"
                            value={purchaseNote}
                            onChange={(e) => setPurchaseNote(e.target.value)}
                            placeholder="e.g. ordered on Amazon"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                        />
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reveal date (optional)</label>
                        <input
                            type="date"
                            value={revealDate}
                            onChange={(e) => setRevealDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex gap-2 justify-end mt-4">
                            <button onClick={() => setPurchaseTarget(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
                            <button onClick={confirmPurchase} disabled={busyId === purchaseTarget.id} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Suggest-to compose modal — prefilled from the browsed item; the
                sender picks which group member to suggest it to. */}
            {suggestFrom && (
                <SuggestIdeaModal
                    prefill={{
                        sourceWishId: suggestFrom.id,
                        groupId: suggestFrom.groups?.[0]?.id,
                        title: suggestFrom.title,
                        url: suggestFrom.url || '',
                        imageUrl: suggestFrom.image_url || '',
                        priceMin: suggestFrom.price_min ?? '',
                        priceMax: suggestFrom.price_max ?? '',
                        categoryId: suggestFrom.category_id ?? suggestFrom.category?.id,
                    }}
                    notify={notify}
                    onClose={() => setSuggestFrom(null)}
                    onSaved={() => setSuggestFrom(null)}
                />
            )}
        </AppLayout>
    );
}

function FilterButton({ label, active, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`px-3 py-1 rounded-full text-sm font-medium ${active ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
        >
            {label}
        </button>
    );
}
